import type { Template } from "../types/templates.types";

export const simpleCleaningInit: Template = {
    id: "simple-cleaning",
    name: "Simple Cleaning Services",
    description:
        "Elegant, professional landing page for a cleaning business. Navy & teal colour palette. Includes sticky header, hero with stats, six-service grid, about section, testimonials, contact form with map embed, and a full footer.",
    html: `
    <!-- ── HEADER ─────────────────────────────────────────── -->
    <header class="pc-header">
      <nav class="pc-nav">
        <div class="pc-nav-logo">
          <span class="pc-nav-logo-mark">P&amp;Co.</span>
          <span class="pc-nav-logo-sub">Pristine &amp; Co.</span>
        </div>
        <ul class="pc-nav-links">
          <li><a href="#pc-services">Services</a></li>
          <li><a href="#pc-about">About</a></li>
          <li><a href="#pc-reviews">Reviews</a></li>
          <li><a href="#pc-contact">Contact</a></li>
        </ul>
        <a href="#pc-contact" class="pc-nav-cta">Get a Quote</a>
      </nav>

      <div class="pc-hero">
        <div class="pc-hero-eyebrow">Trusted. Thorough. Pristine.</div>
        <h1 class="pc-hero-headline">
          A cleaner space.<br />
          <em>A clearer mind.</em>
        </h1>
        <p class="pc-hero-body">
          Professional cleaning services tailored to homes, offices, and commercial spaces.
          We bring precision, care, and a spotless finish — every single time.
        </p>
        <div class="pc-hero-actions">
          <a href="#pc-services" class="pc-btn pc-btn-primary">Our Services</a>
          <a href="#pc-about" class="pc-btn pc-btn-ghost">Learn More</a>
        </div>
        <div class="pc-hero-stat-row">
          <div class="pc-hero-stat">
            <span class="pc-hero-stat-num">12+</span>
            <span class="pc-hero-stat-label">Years of Experience</span>
          </div>
          <div class="pc-hero-stat-divider"></div>
          <div class="pc-hero-stat">
            <span class="pc-hero-stat-num">3,400+</span>
            <span class="pc-hero-stat-label">Happy Clients</span>
          </div>
          <div class="pc-hero-stat-divider"></div>
          <div class="pc-hero-stat">
            <span class="pc-hero-stat-num">100%</span>
            <span class="pc-hero-stat-label">Satisfaction Guaranteed</span>
          </div>
        </div>
      </div>

      <div class="pc-hero-accent-bar"></div>
    </header>

    <!-- ── MAIN ───────────────────────────────────────────── -->
    <main>

      <!-- SERVICES -->
      <section class="pc-section" id="pc-services">
        <div class="pc-container">
          <div class="pc-section-label">What We Do</div>
          <h2 class="pc-section-title">Our Core Services</h2>
          <p class="pc-section-sub">From deep-cleaning your home to maintaining your office, we have a service crafted for every need.</p>

          <div class="pc-services-grid">

            <div class="pc-service-card">
              <div class="pc-service-card-icon">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="6" y="14" width="36" height="26" rx="3" stroke="currentColor" stroke-width="2.5"/>
                  <path d="M16 14V10a8 8 0 0 1 16 0v4" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                  <path d="M18 26l4 4 8-8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="pc-service-card-title">Residential Cleaning</h3>
              <p class="pc-service-card-body">Regular or one-time deep cleans for apartments, condos, and family homes. We treat your space like our own.</p>
              <a href="#pc-contact" class="pc-service-card-link">Request a clean →</a>
            </div>

            <div class="pc-service-card pc-service-card-featured">
              <div class="pc-service-card-badge">Most Popular</div>
              <div class="pc-service-card-icon">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="4" y="10" width="40" height="28" rx="3" stroke="currentColor" stroke-width="2.5"/>
                  <path d="M4 18h40" stroke="currentColor" stroke-width="2.5"/>
                  <path d="M14 28h20M14 33h12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                </svg>
              </div>
              <h3 class="pc-service-card-title">Office &amp; Commercial</h3>
              <p class="pc-service-card-body">Maintain a productive, hygienic environment for your team with scheduled professional cleaning plans.</p>
              <a href="#pc-contact" class="pc-service-card-link">Request a clean →</a>
            </div>

            <div class="pc-service-card">
              <div class="pc-service-card-icon">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 6L6 18v24h12V30h12v12h12V18L24 6z" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="pc-service-card-title">Move-In / Move-Out</h3>
              <p class="pc-service-card-body">Start fresh or leave your old place spotless. Our thorough end-of-tenancy cleans tick every landlord checklist item.</p>
              <a href="#pc-contact" class="pc-service-card-link">Request a clean →</a>
            </div>

            <div class="pc-service-card">
              <div class="pc-service-card-icon">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="18" stroke="currentColor" stroke-width="2.5"/>
                  <path d="M24 14v10l6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <h3 class="pc-service-card-title">Post-Construction</h3>
              <p class="pc-service-card-body">Dust, debris, and residue cleared efficiently after renovations so you can enjoy your newly built or refurbished space sooner.</p>
              <a href="#pc-contact" class="pc-service-card-link">Request a clean →</a>
            </div>

            <div class="pc-service-card">
              <div class="pc-service-card-icon">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 36c4-8 12-8 16 0s12 8 16 0" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                  <path d="M8 26c4-8 12-8 16 0s12 8 16 0" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                  <path d="M8 16c4-8 12-8 16 0s12 8 16 0" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                </svg>
              </div>
              <h3 class="pc-service-card-title">Carpet &amp; Upholstery</h3>
              <p class="pc-service-card-body">Hot-water extraction and professional-grade treatment that brings carpets and upholstery back to like-new condition.</p>
              <a href="#pc-contact" class="pc-service-card-link">Request a clean →</a>
            </div>

            <div class="pc-service-card">
              <div class="pc-service-card-icon">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 38V20l12-12 12 12v18" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round"/>
                  <rect x="19" y="28" width="10" height="10" rx="1" stroke="currentColor" stroke-width="2.5"/>
                  <path d="M4 38h40" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                </svg>
              </div>
              <h3 class="pc-service-card-title">Window &amp; Facade</h3>
              <p class="pc-service-card-body">Crystal-clear windows inside and out. We use streak-free methods for residential windows and low-rise commercial facades.</p>
              <a href="#pc-contact" class="pc-service-card-link">Request a clean →</a>
            </div>

          </div>
        </div>
      </section>

      <!-- ABOUT -->
      <section class="pc-section pc-section-navy" id="pc-about">
        <div class="pc-container pc-about-grid">
          <div class="pc-about-visual">
            <div class="pc-about-image-frame">
              <div class="pc-about-image-placeholder">
                <svg viewBox="0 0 200 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0" y="0" width="200" height="240" rx="4" fill="#1a3045"/>
                  <circle cx="100" cy="90" r="34" stroke="#4ECDC4" stroke-width="2"/>
                  <path d="M66 90 Q100 130 134 90" stroke="#4ECDC4" stroke-width="2"/>
                  <path d="M60 150 Q100 120 140 150 Q160 190 100 200 Q40 190 60 150Z" fill="#4ECDC4" fill-opacity="0.15" stroke="#4ECDC4" stroke-width="1.5"/>
                  <circle cx="100" cy="84" r="18" fill="#4ECDC4" fill-opacity="0.15"/>
                  <text x="100" y="230" text-anchor="middle" fill="#4ECDC4" font-size="11" font-family="sans-serif" opacity="0.7">Your Team Photo Here</text>
                </svg>
              </div>
              <div class="pc-about-tag">Est. 2012</div>
            </div>
          </div>

          <div class="pc-about-content">
            <div class="pc-section-label pc-section-label-light">Who We Are</div>
            <h2 class="pc-section-title pc-section-title-light">Built on trust,<br/><em>driven by detail.</em></h2>
            <p class="pc-about-text">
              Pristine &amp; Co. started with a simple belief: a truly clean environment changes how people feel. Founded in 2012 by Maria and Daniel Torres, we've grown from a two-person operation into a trusted team of over 40 certified cleaning specialists.
            </p>
            <p class="pc-about-text">
              We use only eco-certified, non-toxic products — safe for your family, your pets, and the planet. Every team member is background-checked, fully insured, and trained to our exacting standards.
            </p>

            <div class="pc-about-pillars">
              <div class="pc-pillar">
                <div class="pc-pillar-dot"></div>
                <div>
                  <strong>Eco-Certified Products</strong>
                  <p>Plant-derived, biodegradable solutions that clean without compromise.</p>
                </div>
              </div>
              <div class="pc-pillar">
                <div class="pc-pillar-dot"></div>
                <div>
                  <strong>Fully Insured Team</strong>
                  <p>Every specialist is vetted, trained, and covered — giving you total peace of mind.</p>
                </div>
              </div>
              <div class="pc-pillar">
                <div class="pc-pillar-dot"></div>
                <div>
                  <strong>Flexible Scheduling</strong>
                  <p>Early mornings, evenings, weekends — we work around your life, not the other way.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- REVIEWS -->
      <section class="pc-section" id="pc-reviews">
        <div class="pc-container">
          <div class="pc-section-label">What Clients Say</div>
          <h2 class="pc-section-title">Stories from our community</h2>

          <div class="pc-reviews-grid">

            <div class="pc-review-card">
              <div class="pc-review-stars">★★★★★</div>
              <p class="pc-review-text">"I've tried four cleaning services over the years — Pristine &amp; Co. is on a completely different level. They're thorough, punctual, and genuinely care about the result."</p>
              <div class="pc-review-author">
                <div class="pc-review-avatar pc-review-avatar-a">LM</div>
                <div>
                  <strong>Laura M.</strong>
                  <span>Homeowner, Brooklyn</span>
                </div>
              </div>
            </div>

            <div class="pc-review-card pc-review-card-accent">
              <div class="pc-review-stars">★★★★★</div>
              <p class="pc-review-text">"We hired them for our office and couldn't be happier. The team is discreet, fast, and leaves the space looking incredible. Our employees noticed the difference right away."</p>
              <div class="pc-review-author">
                <div class="pc-review-avatar pc-review-avatar-b">JR</div>
                <div>
                  <strong>James R.</strong>
                  <span>Operations Manager, Norwell Inc.</span>
                </div>
              </div>
            </div>

            <div class="pc-review-card">
              <div class="pc-review-stars">★★★★★</div>
              <p class="pc-review-text">"Move-out cleaning done in 3 hours, deposit fully returned. Worth every single penny. The landlord literally said it looked better than move-in day."</p>
              <div class="pc-review-author">
                <div class="pc-review-avatar pc-review-avatar-c">SC</div>
                <div>
                  <strong>Sofia C.</strong>
                  <span>Former Tenant, Manhattan</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <!-- CONTACT + MAP -->
      <section class="pc-section pc-section-light" id="pc-contact">
        <div class="pc-container pc-contact-grid">

          <div class="pc-contact-info">
            <div class="pc-section-label">Get In Touch</div>
            <h2 class="pc-section-title">Let's get your<br/><em>space pristine.</em></h2>
            <p class="pc-contact-intro">Fill out the form and we'll get back to you within 24 hours with a no-obligation quote tailored to your needs.</p>

            <div class="pc-contact-details">
              <div class="pc-contact-detail-item">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>+1 (212) 555-0192</span>
              </div>
              <div class="pc-contact-detail-item">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <polyline points="22,6 12,13 2,6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>hello@pristineandco.com</span>
              </div>
              <div class="pc-contact-detail-item">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="12" cy="10" r="3" stroke="currentColor" stroke-width="1.8"/>
                </svg>
                <span>148 West 37th St, New York, NY 10018</span>
              </div>
            </div>
          </div>

          <form class="pc-contact-form" action="#pc-contact" method="GET">
            <div class="pc-form-row">
              <div class="pc-form-group">
                <label for="pc-fname">First Name</label>
                <input type="text" id="pc-fname" name="fname" placeholder="Maria" required />
              </div>
              <div class="pc-form-group">
                <label for="pc-lname">Last Name</label>
                <input type="text" id="pc-lname" name="lname" placeholder="Torres" required />
              </div>
            </div>
            <div class="pc-form-group">
              <label for="pc-email">Email Address</label>
              <input type="email" id="pc-email" name="email" placeholder="maria@example.com" required />
            </div>
            <div class="pc-form-group">
              <label for="pc-phone">Phone Number</label>
              <input type="tel" id="pc-phone" name="phone" placeholder="+1 (212) 000-0000" />
            </div>
            <div class="pc-form-group">
              <label for="pc-service">Service Needed</label>
              <select id="pc-service" name="service">
                <option value="">Select a service…</option>
                <option>Residential Cleaning</option>
                <option>Office &amp; Commercial</option>
                <option>Move-In / Move-Out</option>
                <option>Post-Construction</option>
                <option>Carpet &amp; Upholstery</option>
                <option>Window &amp; Facade</option>
              </select>
            </div>
            <div class="pc-form-group">
              <label for="pc-message">Message</label>
              <textarea id="pc-message" name="message" rows="4" placeholder="Tell us about your space and any specific requirements…"></textarea>
            </div>
            <button type="submit" class="pc-btn pc-btn-primary pc-btn-full">Send My Request</button>
          </form>

        </div>

        <div class="pc-container">
          <div class="pc-map-wrapper">
            <iframe
              title="Pristine &amp; Co. Location"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-74.0000%2C40.7484%2C-73.9800%2C40.7584&layer=mapnik&marker=40.7534%2C-73.9900"
              allowfullscreen
              loading="lazy"
            ></iframe>
            <div class="pc-map-badge">Our Office</div>
          </div>
        </div>
      </section>

    </main>

    <!-- ── FOOTER ──────────────────────────────────────────── -->
    <footer class="pc-footer">
      <div class="pc-container pc-footer-grid">

        <div class="pc-footer-brand">
          <div class="pc-footer-logo">P&amp;Co.</div>
          <p class="pc-footer-tagline">Professional cleaning services for homes, offices, and beyond — delivered with care and precision.</p>
          <div class="pc-footer-social">
            <a href="#" aria-label="Instagram" class="pc-social-link">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="20" height="20" rx="5" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.8"/><circle cx="17.5" cy="6.5" r="1" fill="currentColor"/></svg>
            </a>
            <a href="#" aria-label="Facebook" class="pc-social-link">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </a>
            <a href="#" aria-label="LinkedIn" class="pc-social-link">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><rect x="2" y="9" width="4" height="12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><circle cx="4" cy="4" r="2" stroke="currentColor" stroke-width="1.8"/></svg>
            </a>
          </div>
        </div>

        <div class="pc-footer-col">
          <h4>Services</h4>
          <ul>
            <li><a href="#pc-services">Residential Cleaning</a></li>
            <li><a href="#pc-services">Office &amp; Commercial</a></li>
            <li><a href="#pc-services">Move-In / Move-Out</a></li>
            <li><a href="#pc-services">Post-Construction</a></li>
            <li><a href="#pc-services">Carpet &amp; Upholstery</a></li>
            <li><a href="#pc-services">Window &amp; Facade</a></li>
          </ul>
        </div>

        <div class="pc-footer-col">
          <h4>Company</h4>
          <ul>
            <li><a href="#pc-about">About Us</a></li>
            <li><a href="#pc-reviews">Testimonials</a></li>
            <li><a href="#">Careers</a></li>
            <li><a href="#">Blog</a></li>
            <li><a href="#pc-contact">Contact</a></li>
          </ul>
        </div>

        <div class="pc-footer-col">
          <h4>Hours</h4>
          <ul class="pc-hours-list">
            <li><span>Mon – Fri</span><span>7:00 AM – 8:00 PM</span></li>
            <li><span>Saturday</span><span>8:00 AM – 6:00 PM</span></li>
            <li><span>Sunday</span><span>9:00 AM – 4:00 PM</span></li>
          </ul>
        </div>

      </div>

      <div class="pc-footer-bottom">
        <div class="pc-container pc-footer-bottom-inner">
          <span>© 2024 Pristine &amp; Co. All rights reserved.</span>
          <div class="pc-footer-legal">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  `,
    css: `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,400&family=Source+Sans+3:wght@300;400;600&display=swap');

    /* ── RESET ──────────────────────────────────────────── */
    *, *::before, *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }

    /* ── LAYOUT UTILITIES ───────────────────────────────── */
    .pc-container {
      max-width: 1160px;
      margin: 0 auto;
      padding: 0 32px;
    }

    .pc-section {
      padding: 96px 0;
      background: #F5F7F9;
      font-family: 'Source Sans 3', sans-serif;
    }

    .pc-section-navy {
      background: #0F1F2E;
    }

    .pc-section-light {
      background: #FFFFFF;
    }

    .pc-section-label {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: #38b2aa;
      margin-bottom: 12px;
    }

    .pc-section-label-light {
      color: #4ECDC4;
    }

    .pc-section-title {
      font-family: 'Playfair Display', serif;
      font-size: clamp(2rem, 3.5vw, 2.8rem);
      line-height: 1.2;
      color: #1C2B3A;
      margin-bottom: 16px;
    }

    .pc-section-title em {
      font-style: italic;
      color: #38b2aa;
    }

    .pc-section-title-light {
      color: #FFFFFF;
    }

    .pc-section-title-light em {
      color: #4ECDC4;
    }

    .pc-section-sub {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 1.05rem;
      color: #4A607A;
      max-width: 560px;
      margin-bottom: 56px;
      line-height: 1.7;
    }

    /* ── BUTTONS ────────────────────────────────────────── */
    .pc-btn {
      display: inline-block;
      padding: 14px 32px;
      border-radius: 6px;
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.25s ease;
      border: 2px solid transparent;
      letter-spacing: 0.02em;
      text-decoration: none;
    }

    .pc-btn-primary {
      background: #4ECDC4;
      color: #0F1F2E;
    }

    .pc-btn-primary:hover {
      background: #38b2aa;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(78, 205, 196, 0.35);
    }

    .pc-btn-ghost {
      background: transparent;
      border-color: rgba(255, 255, 255, 0.45);
      color: #FFFFFF;
    }

    .pc-btn-ghost:hover {
      border-color: #4ECDC4;
      color: #4ECDC4;
    }

    .pc-btn-full {
      width: 100%;
      text-align: center;
    }

    /* ── NAV ────────────────────────────────────────────── */
    .pc-header {
      background: #0F1F2E;
      position: relative;
      overflow: hidden;
    }

    .pc-header::before {
      content: '';
      position: absolute;
      top: -120px;
      right: -120px;
      width: 600px;
      height: 600px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(78, 205, 196, 0.06) 0%, transparent 70%);
      pointer-events: none;
    }

    .pc-header::after {
      content: '';
      position: absolute;
      bottom: 60px;
      left: -80px;
      width: 400px;
      height: 400px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(78, 205, 196, 0.04) 0%, transparent 70%);
      pointer-events: none;
    }

    .pc-nav {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 28px 32px;
      max-width: 1160px;
      margin: 0 auto;
      position: relative;
      z-index: 10;
    }

    .pc-nav-logo {
      display: flex;
      flex-direction: column;
      line-height: 1;
    }

    .pc-nav-logo-mark {
      font-family: 'Playfair Display', serif;
      font-size: 1.5rem;
      font-weight: 700;
      color: #FFFFFF;
      letter-spacing: -0.02em;
    }

    .pc-nav-logo-sub {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.65rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      color: #4ECDC4;
      font-weight: 600;
    }

    .pc-nav-links {
      display: flex;
      gap: 36px;
      list-style: none;
    }

    .pc-nav-links a {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.9rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      color: rgba(255, 255, 255, 0.75);
      text-decoration: none;
      transition: color 0.2s;
    }

    .pc-nav-links a:hover {
      color: #4ECDC4;
    }

    .pc-nav-cta {
      background: #4ECDC4;
      color: #0F1F2E;
      padding: 10px 24px;
      border-radius: 6px;
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.875rem;
      font-weight: 600;
      text-decoration: none;
      transition: all 0.25s;
    }

    .pc-nav-cta:hover {
      background: #FFFFFF;
      transform: translateY(-1px);
    }

    /* ── HERO ───────────────────────────────────────────── */
    .pc-hero {
      position: relative;
      z-index: 2;
      padding: 64px 32px 80px;
      max-width: 1160px;
      margin: 0 auto;
    }

    .pc-hero-eyebrow {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.78rem;
      font-weight: 600;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: #4ECDC4;
      margin-bottom: 20px;
      animation: pc-fadeUp 0.7s ease both;
    }

    .pc-hero-headline {
      font-family: 'Playfair Display', serif;
      font-size: clamp(2.8rem, 6vw, 5rem);
      color: #FFFFFF;
      font-weight: 700;
      max-width: 720px;
      margin-bottom: 24px;
      animation: pc-fadeUp 0.7s 0.1s ease both;
      line-height: 1.08;
    }

    .pc-hero-headline em {
      color: #4ECDC4;
      font-style: italic;
    }

    .pc-hero-body {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 1.1rem;
      color: rgba(255, 255, 255, 0.68);
      max-width: 520px;
      margin-bottom: 40px;
      animation: pc-fadeUp 0.7s 0.2s ease both;
      line-height: 1.75;
    }

    .pc-hero-actions {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
      margin-bottom: 64px;
      animation: pc-fadeUp 0.7s 0.3s ease both;
    }

    .pc-hero-stat-row {
      display: flex;
      align-items: center;
      gap: 32px;
      flex-wrap: wrap;
      animation: pc-fadeUp 0.7s 0.4s ease both;
      padding-top: 40px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .pc-hero-stat {
      display: flex;
      flex-direction: column;
    }

    .pc-hero-stat-num {
      font-family: 'Playfair Display', serif;
      font-size: 2rem;
      font-weight: 700;
      color: #FFFFFF;
      line-height: 1;
    }

    .pc-hero-stat-label {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.8rem;
      color: rgba(255, 255, 255, 0.5);
      margin-top: 4px;
      letter-spacing: 0.04em;
    }

    .pc-hero-stat-divider {
      width: 1px;
      height: 40px;
      background: rgba(255, 255, 255, 0.15);
    }

    .pc-hero-accent-bar {
      position: relative;
      height: 64px;
      background: #0F1F2E;
      clip-path: polygon(0 0, 100% 0, 100% 20%, 0 100%);
      margin-bottom: -2px;
    }

    /* ── SERVICES ───────────────────────────────────────── */
    .pc-services-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 24px;
    }

    .pc-service-card {
      background: #FFFFFF;
      border: 1px solid #E4EAF0;
      border-radius: 10px;
      padding: 36px 32px;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
      animation: pc-fadeUp 0.55s ease both;
    }

    .pc-service-card:nth-child(1) { animation-delay: 0.05s; }
    .pc-service-card:nth-child(2) { animation-delay: 0.13s; }
    .pc-service-card:nth-child(3) { animation-delay: 0.21s; }
    .pc-service-card:nth-child(4) { animation-delay: 0.29s; }
    .pc-service-card:nth-child(5) { animation-delay: 0.37s; }
    .pc-service-card:nth-child(6) { animation-delay: 0.45s; }

    .pc-service-card::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background: #E4EAF0;
      transition: background 0.3s;
    }

    .pc-service-card:hover {
      border-color: #4ECDC4;
      box-shadow: 0 12px 48px rgba(15, 31, 46, 0.16);
      transform: translateY(-4px);
    }

    .pc-service-card:hover::before {
      background: #4ECDC4;
    }

    .pc-service-card-featured {
      border-color: #4ECDC4;
      background: linear-gradient(135deg, #f0fefd 0%, #FFFFFF 100%);
    }

    .pc-service-card-featured::before {
      background: #4ECDC4;
    }

    .pc-service-card-badge {
      display: inline-block;
      background: #4ECDC4;
      color: #0F1F2E;
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.7rem;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      padding: 4px 10px;
      border-radius: 100px;
      margin-bottom: 16px;
    }

    .pc-service-card-icon {
      width: 48px;
      height: 48px;
      color: #38b2aa;
      margin-bottom: 20px;
    }

    .pc-service-card-icon svg {
      width: 100%;
      height: 100%;
    }

    .pc-service-card-title {
      font-family: 'Playfair Display', serif;
      font-size: 1.2rem;
      color: #1C2B3A;
      margin-bottom: 10px;
    }

    .pc-service-card-body {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.95rem;
      color: #4A607A;
      margin-bottom: 20px;
      line-height: 1.65;
    }

    .pc-service-card-link {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.875rem;
      font-weight: 600;
      color: #38b2aa;
      text-decoration: none;
      letter-spacing: 0.02em;
      transition: color 0.2s;
    }

    .pc-service-card-link:hover {
      color: #0F1F2E;
    }

    /* ── ABOUT ──────────────────────────────────────────── */
    .pc-about-grid {
      display: grid;
      grid-template-columns: 1fr 1.4fr;
      gap: 80px;
      align-items: center;
    }

    .pc-about-image-frame {
      position: relative;
    }

    .pc-about-image-placeholder {
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 12px 48px rgba(15, 31, 46, 0.16);
    }

    .pc-about-image-placeholder svg {
      width: 100%;
      height: auto;
      display: block;
    }

    .pc-about-tag {
      position: absolute;
      bottom: -20px;
      right: -20px;
      background: #4ECDC4;
      color: #0F1F2E;
      font-family: 'Playfair Display', serif;
      font-size: 1.1rem;
      font-weight: 700;
      padding: 16px 24px;
      border-radius: 6px;
      box-shadow: 0 4px 24px rgba(15, 31, 46, 0.10);
    }

    .pc-about-content .pc-section-title {
      margin-bottom: 20px;
    }

    .pc-about-text {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 1rem;
      line-height: 1.8;
      margin-bottom: 16px;
      color: rgba(255, 255, 255, 0.68);
    }

    .pc-about-pillars {
      margin-top: 36px;
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .pc-pillar {
      display: flex;
      gap: 16px;
      align-items: flex-start;
      animation: pc-fadeUp 0.55s ease both;
    }

    .pc-pillar:nth-child(1) { animation-delay: 0.1s; }
    .pc-pillar:nth-child(2) { animation-delay: 0.2s; }
    .pc-pillar:nth-child(3) { animation-delay: 0.3s; }

    .pc-pillar-dot {
      flex-shrink: 0;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #4ECDC4;
      margin-top: 7px;
    }

    .pc-pillar strong {
      display: block;
      color: #FFFFFF;
      margin-bottom: 4px;
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.975rem;
    }

    .pc-pillar p {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.55);
      line-height: 1.6;
      margin: 0;
    }

    /* ── REVIEWS ────────────────────────────────────────── */
    .pc-reviews-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 24px;
      margin-top: 48px;
    }

    .pc-review-card {
      background: #FFFFFF;
      border: 1px solid #E4EAF0;
      border-radius: 10px;
      padding: 36px 32px;
      transition: box-shadow 0.3s ease, transform 0.3s ease;
      animation: pc-fadeUp 0.55s ease both;
    }

    .pc-review-card:nth-child(1) { animation-delay: 0.05s; }
    .pc-review-card:nth-child(2) { animation-delay: 0.15s; }
    .pc-review-card:nth-child(3) { animation-delay: 0.25s; }

    .pc-review-card:hover {
      box-shadow: 0 12px 48px rgba(15, 31, 46, 0.16);
      transform: translateY(-3px);
    }

    .pc-review-card-accent {
      background: #0F1F2E;
      border-color: #0F1F2E;
    }

    .pc-review-card-accent .pc-review-text {
      color: rgba(255, 255, 255, 0.72);
    }

    .pc-review-card-accent .pc-review-author strong {
      color: #FFFFFF;
    }

    .pc-review-card-accent .pc-review-author span {
      color: rgba(255, 255, 255, 0.45);
    }

    .pc-review-stars {
      font-size: 1rem;
      color: #4ECDC4;
      letter-spacing: 2px;
      margin-bottom: 16px;
    }

    .pc-review-text {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.975rem;
      color: #4A607A;
      line-height: 1.75;
      margin-bottom: 24px;
      font-style: italic;
    }

    .pc-review-author {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .pc-review-avatar {
      width: 42px;
      height: 42px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.8rem;
      font-weight: 700;
      flex-shrink: 0;
    }

    .pc-review-avatar-a { background: #d0f0ee; color: #38b2aa; }
    .pc-review-avatar-b { background: #4ECDC4; color: #0F1F2E; }
    .pc-review-avatar-c { background: #d8e8f4; color: #17304A; }

    .pc-review-author strong {
      display: block;
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.925rem;
      color: #1C2B3A;
    }

    .pc-review-author span {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.8rem;
      color: #7A94AD;
    }

    /* ── CONTACT ────────────────────────────────────────── */
    .pc-contact-grid {
      display: grid;
      grid-template-columns: 1fr 1.2fr;
      gap: 80px;
      align-items: start;
      margin-bottom: 64px;
    }

    .pc-contact-intro {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 1rem;
      color: #4A607A;
      margin-bottom: 36px;
      line-height: 1.7;
    }

    .pc-contact-details {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .pc-contact-detail-item {
      display: flex;
      align-items: center;
      gap: 12px;
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.925rem;
      color: #4A607A;
    }

    .pc-contact-detail-item svg {
      width: 20px;
      height: 20px;
      color: #38b2aa;
      flex-shrink: 0;
    }

    /* ── FORM ───────────────────────────────────────────── */
    .pc-contact-form {
      background: #FFFFFF;
      border: 1px solid #E4EAF0;
      border-radius: 12px;
      padding: 44px 40px;
      box-shadow: 0 4px 24px rgba(15, 31, 46, 0.10);
    }

    .pc-form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }

    .pc-form-group {
      display: flex;
      flex-direction: column;
      margin-bottom: 20px;
    }

    .pc-form-group label {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.82rem;
      font-weight: 600;
      letter-spacing: 0.05em;
      color: #1C2B3A;
      margin-bottom: 8px;
      text-transform: uppercase;
    }

    .pc-form-group input,
    .pc-form-group select,
    .pc-form-group textarea {
      border: 1.5px solid #E4EAF0;
      border-radius: 6px;
      padding: 12px 16px;
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.95rem;
      color: #1C2B3A;
      background: #F5F7F9;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
      -webkit-appearance: none;
      appearance: none;
    }

    .pc-form-group input:focus,
    .pc-form-group select:focus,
    .pc-form-group textarea:focus {
      border-color: #4ECDC4;
      box-shadow: 0 0 0 3px rgba(78, 205, 196, 0.15);
      background: #FFFFFF;
    }

    .pc-form-group input::placeholder,
    .pc-form-group textarea::placeholder {
      color: #7A94AD;
    }

    .pc-form-group select {
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%234A607A' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 14px center;
      background-color: #F5F7F9;
      padding-right: 40px;
      cursor: pointer;
    }

    .pc-form-group textarea {
      resize: vertical;
    }

    /* ── MAP ────────────────────────────────────────────── */
    .pc-map-wrapper {
      position: relative;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 4px 24px rgba(15, 31, 46, 0.10);
      border: 1px solid #E4EAF0;
      height: 380px;
    }

    .pc-map-wrapper iframe {
      width: 100%;
      height: 100%;
      border: 0;
      display: block;
    }

    .pc-map-badge {
      position: absolute;
      top: 20px;
      left: 20px;
      background: #0F1F2E;
      color: #FFFFFF;
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.8rem;
      font-weight: 600;
      padding: 8px 16px;
      border-radius: 100px;
      letter-spacing: 0.06em;
      box-shadow: 0 4px 24px rgba(15, 31, 46, 0.10);
    }

    /* ── FOOTER ─────────────────────────────────────────── */
    .pc-footer {
      background: #0F1F2E;
      color: rgba(255, 255, 255, 0.6);
      font-family: 'Source Sans 3', sans-serif;
    }

    .pc-footer-grid {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1.2fr;
      gap: 56px;
      padding-top: 72px;
      padding-bottom: 56px;
    }

    .pc-footer-logo {
      font-family: 'Playfair Display', serif;
      font-size: 2rem;
      font-weight: 700;
      color: #FFFFFF;
      letter-spacing: -0.02em;
      margin-bottom: 14px;
    }

    .pc-footer-tagline {
      font-size: 0.9rem;
      line-height: 1.7;
      color: rgba(255, 255, 255, 0.5);
      max-width: 280px;
      margin-bottom: 24px;
    }

    .pc-footer-social {
      display: flex;
      gap: 12px;
    }

    .pc-social-link {
      width: 38px;
      height: 38px;
      border-radius: 50%;
      border: 1px solid rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(255, 255, 255, 0.55);
      text-decoration: none;
      transition: all 0.25s;
    }

    .pc-social-link svg {
      width: 16px;
      height: 16px;
    }

    .pc-social-link:hover {
      border-color: #4ECDC4;
      color: #4ECDC4;
    }

    .pc-footer-col h4 {
      font-family: 'Source Sans 3', sans-serif;
      font-size: 0.78rem;
      font-weight: 600;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: #FFFFFF;
      margin-bottom: 20px;
    }

    .pc-footer-col ul {
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .pc-footer-col ul li a {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.5);
      text-decoration: none;
      transition: color 0.2s;
    }

    .pc-footer-col ul li a:hover {
      color: #4ECDC4;
    }

    .pc-hours-list li {
      display: flex;
      justify-content: space-between;
      font-size: 0.875rem;
      gap: 16px;
    }

    .pc-hours-list li span:first-child {
      color: rgba(255, 255, 255, 0.55);
    }

    .pc-hours-list li span:last-child {
      color: rgba(255, 255, 255, 0.85);
      font-weight: 600;
    }

    .pc-footer-bottom {
      border-top: 1px solid rgba(255, 255, 255, 0.08);
    }

    .pc-footer-bottom-inner {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px 32px;
      font-size: 0.82rem;
      color: rgba(255, 255, 255, 0.35);
    }

    .pc-footer-legal {
      display: flex;
      gap: 24px;
    }

    .pc-footer-legal a {
      color: rgba(255, 255, 255, 0.35);
      text-decoration: none;
      transition: color 0.2s;
    }

    .pc-footer-legal a:hover {
      color: #4ECDC4;
    }

    /* ── ANIMATIONS ─────────────────────────────────────── */
    @keyframes pc-fadeUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* ── RESPONSIVE ─────────────────────────────────────── */
    @media (max-width: 960px) {
      .pc-about-grid {
        grid-template-columns: 1fr;
        gap: 48px;
      }

      .pc-about-visual {
        max-width: 380px;
        margin: 0 auto;
      }

      .pc-footer-grid {
        grid-template-columns: 1fr 1fr;
        gap: 40px;
      }

      .pc-footer-brand {
        grid-column: 1 / -1;
      }

      .pc-contact-grid {
        grid-template-columns: 1fr;
        gap: 48px;
      }
    }

    @media (max-width: 720px) {
      .pc-nav-links {
        display: none;
      }

      .pc-hero {
        padding: 40px 24px 60px;
      }

      .pc-hero-headline {
        font-size: 2.4rem;
      }

      .pc-hero-stat-divider {
        display: none;
      }

      .pc-section {
        padding: 64px 0;
      }

      .pc-services-grid {
        grid-template-columns: 1fr;
      }

      .pc-form-row {
        grid-template-columns: 1fr;
      }

      .pc-contact-form {
        padding: 32px 24px;
      }

      .pc-footer-grid {
        grid-template-columns: 1fr;
        gap: 32px;
        padding-top: 48px;
      }

      .pc-footer-bottom-inner {
        flex-direction: column;
        gap: 12px;
        text-align: center;
      }
    }

    @media (max-width: 480px) {
      .pc-hero-actions {
        flex-direction: column;
      }
    }
  `,
    thumbnailUrl: "",
    blocks: [],
};